import { useContext, useMemo } from 'react'
import { ItemSelectedForCropContext } from './contexts'
import type { TimelineItem } from './types'

/** Crop geometry shared by the layers and the editor (Editor Starter port). */

export type CanHaveCrop = {
  cropLeft: number
  cropTop: number
  cropRight: number
  cropBottom: number
}

const canCropMap = {
  video: true,
  image: true,
  text: false,
  solid: false,
  audio: false,
  captions: false,
  component: false,
  // Ours (#541): a scene draws nothing; its view is the picture it crops.
  scene: false,
  view: true,
} satisfies Record<TimelineItem['type'], boolean>

export type CroppableItem = Extract<
  TimelineItem,
  { type: 'video' | 'image' | 'view' }
>

export function getCanCrop(item: TimelineItem): item is CroppableItem {
  return canCropMap[item.type]
}

export function getCropFromItem(item: TimelineItem): CanHaveCrop | null {
  if (!getCanCrop(item)) return null
  const cropLeft = item.cropLeft ?? 0
  const cropTop = item.cropTop ?? 0
  const cropRight = item.cropRight ?? 0
  const cropBottom = item.cropBottom ?? 0
  return {
    cropLeft,
    cropTop,
    // Total crop per direction must leave at least 1px of the item visible.
    cropRight: Math.min(cropRight, 1 - cropLeft - 1 / item.width),
    cropBottom: Math.min(cropBottom, 1 - cropTop - 1 / item.height),
  }
}

export function getCropSizeFactors(item: TimelineItem) {
  const crop = getCropFromItem(item)
  if (!crop) return { width: 1, height: 1 }
  return {
    width: 1 - crop.cropLeft - crop.cropRight,
    height: 1 - crop.cropTop - crop.cropBottom,
  }
}

export function getRectAfterCrop(item: TimelineItem) {
  const crop = getCropFromItem(item)
  if (!crop) {
    return {
      left: item.left,
      top: item.top,
      width: item.width,
      height: item.height,
    }
  }
  const factors = getCropSizeFactors(item)
  return {
    left: item.left + crop.cropLeft * item.width,
    top: item.top + crop.cropTop * item.height,
    width: item.width * factors.width,
    height: item.height * factors.height,
  }
}

export function preserveRectAfterCropPosition<T extends TimelineItem>({
  oldItem,
  newItem,
}: {
  oldItem: TimelineItem
  newItem: T
}): T {
  const oldRect = getRectAfterCrop(oldItem)
  const newRect = getRectAfterCrop(newItem)
  return {
    ...newItem,
    left: newItem.left + oldRect.left - newRect.left,
    top: newItem.top + oldRect.top - newRect.top,
  }
}

/** The editor shows the uncropped item, dimmed, behind the crop being edited. */
export const FEATURE_CROP_BACKGROUNDS = true

export function useCroppableLayer({
  item,
  rotation,
  opacity,
  borderRadius,
  cropBackground,
  includeObjectFitInStyle,
}: {
  item: TimelineItem
  rotation: number
  opacity: number
  borderRadius: number
  cropBackground: boolean
  includeObjectFitInStyle: boolean
}) {
  const itemSelectedForCrop = useContext(ItemSelectedForCropContext)
  const itemIsBeingCropped = item.id === itemSelectedForCrop

  const rectAfterCrop = useMemo(() => {
    if (cropBackground) {
      return {
        left: item.left,
        top: item.top,
        width: item.width,
        height: item.height,
      }
    }
    return getRectAfterCrop(item)
  }, [item, cropBackground])
  const crop = useMemo(() => getCropFromItem(item), [item])
  if (!crop) throw new Error('Crop not implemented for this item type')

  const innerStyle: React.CSSProperties = useMemo(
    () => ({
      width: item.width,
      left: cropBackground ? 0 : -(crop.cropLeft * item.width),
      top: cropBackground ? 0 : -(crop.cropTop * item.height),
      height: item.height,
      position: 'absolute',
      ...(includeObjectFitInStyle ? { objectFit: 'cover' as const } : {}),
      maxWidth: 'unset',
    }),
    [
      crop.cropLeft,
      crop.cropTop,
      item.height,
      item.width,
      cropBackground,
      includeObjectFitInStyle,
    ],
  )

  const outerStyle: React.CSSProperties = useMemo(
    () => ({
      position: 'absolute',
      left: rectAfterCrop.left,
      top: rectAfterCrop.top,
      transform: `rotate(${rotation}deg)`,
      opacity: cropBackground
        ? 0.3
        : itemIsBeingCropped && FEATURE_CROP_BACKGROUNDS
          ? 1
          : opacity,
      overflow: 'hidden',
      width: rectAfterCrop.width,
      height: rectAfterCrop.height,
      borderRadius: cropBackground ? 0 : borderRadius,
    }),
    [
      borderRadius,
      rotation,
      opacity,
      rectAfterCrop,
      itemIsBeingCropped,
      cropBackground,
    ],
  )

  return useMemo(() => ({ innerStyle, outerStyle }), [innerStyle, outerStyle])
}
